/**
 * The guide document contract. A guide URL is a prerendered document — its body
 * and its payload are the same baked file, assembled by
 * `city-page-document.ts` at build or publish time — so the page renders from
 * the payload it was served with and asks the API for nothing.
 *
 * Everything else on the site is a client-rendered route inside the directory's
 * own document. That asymmetry is why a guide URL needs the handoff below: the
 * SPA can walk to a guide URL, but it cannot carry a guide body with it.
 */

import type { CityPage } from "./api";
import { isCityPage } from "./api-response";

/**
 * The prerendered payload, when this document was served as that guide. It is
 * the only source the page renders from, and hydration is byte-identical because
 * both sides read the same JSON. A payload for another slug (or a malformed one)
 * is ignored rather than rendered.
 */
export function readGuideCity(slug: string): CityPage | null {
  const el = document.getElementById("noko-city-page");
  if (!el?.textContent) return null;
  try {
    const parsed: unknown = JSON.parse(el.textContent);
    return isCityPage(parsed) && parsed.slug === slug ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The path this document was loaded from, or null when the browser keeps no
 * navigation timing for it. An in-app navigation changes the address bar without
 * loading a document, so this is the only way to tell "an in-app link walked the
 * SPA here from another route's document" apart from "the server served this URL".
 */
function loadedDocumentPath(): string | null {
  const [entry] = performance.getEntriesByType("navigation");
  if (!entry) return null;
  try {
    return new URL(entry.name).pathname.replace(/\/+$/, "");
  } catch {
    return null;
  }
}

/**
 * Loads the current URL as the document it names, and reports whether it did.
 *
 * Call this when this document carries no payload for the requested guide: then
 * either the server served this URL and there is genuinely no guide to render (an
 * unpublished or unknown slug, which the deployment answers with the directory's
 * own document), or the SPA walked here in-app from another route's document — and
 * no client render can produce a guide body, because the body and the payload are
 * the same file. Only the second is fixed by loading the URL the way a full
 * refresh does, and the document's own load path is what separates them: the
 * handoff must never be a guess, because on the first case it would reload
 * forever. `replace` (not `assign`, as on the way back from a guide) drops the
 * in-app history entry, so Back still reaches the page the reader came from
 * instead of re-entering this route.
 */
export function reloadGuideDocument(): boolean {
  const pathHere = window.location.pathname.replace(/\/+$/, "");
  const loadedPath = loadedDocumentPath();
  if (loadedPath === null || loadedPath === pathHere) return false;
  window.location.replace(window.location.href);
  return true;
}
